import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Copy, Download, Trash2, Power, Users, Bot, Wifi, Shield, ShieldBan, History, Server, Pencil } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard, PageHeader, Spinner, EmptyState, Modal } from "../components/common";
import { DomainGate } from "../components/DomainGate";
import { useLang } from "../context/LanguageContext";
import { TutorialHint } from "../components/TutorialHint";

const fmtDT = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const epTag = (ip) => (ip.watching_media === "tv" && ip.watching_episode ? ` · T${String(ip.watching_season ?? "?").padStart(2, "0")}E${String(ip.watching_episode).padStart(2, "0")}` : "");

export const AccessIps = ({ acc, onBlock, onUnblock, busyIp }) => {
  const ips = acc.ips;
  return (
    <div className="pt-1 space-y-1.5 border-t border-slate-800/60" data-testid={`access-ips-list-${acc.id}`}>
      {ips === undefined && <div className="flex justify-center py-2"><Spinner /></div>}
      {Array.isArray(ips) && ips.length === 0 && <p className="text-[10px] text-slate-500 py-1">Nenhum IP visto nas últimas 24h nesta credencial.</p>}
      {(ips || []).map((ip) => (
        <div key={ip.ip} className="rounded-lg border border-slate-800/60 bg-slate-950/40 px-3 py-2 space-y-1" data-testid={`access-ip-row-${acc.id}-${ip.ip}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono2 text-[11px] text-slate-200">{ip.ip}</span>
            {ip.active && <span className="inline-flex items-center gap-1 text-[9px] font-mono2 uppercase tracking-widest text-emerald-300" data-testid={`access-ip-live-${acc.id}-${ip.ip}`}><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> a ver agora</span>}
            <span className="text-[10px] font-mono2 text-slate-500 truncate">{ip.isp || "ISP ?"}{ip.asn ? ` · AS${ip.asn}` : ""}</span>
            <span className="flex-1" />
            <span className="text-[10px] font-mono2 text-slate-600 shrink-0">{fmtDT(ip.last_seen)}</span>
            {ip.blocked
              ? <button data-testid={`access-ip-unblock-${acc.id}-${ip.ip}`} onClick={() => onUnblock(ip.ip)} disabled={busyIp === ip.ip} className="px-2.5 py-1 rounded-md text-[10px] font-semibold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors shrink-0">{busyIp === ip.ip ? <Spinner /> : "Desbloquear"}</button>
              : <button data-testid={`access-ip-block-${acc.id}-${ip.ip}`} onClick={() => onBlock(ip.ip)} disabled={busyIp === ip.ip} className="px-2.5 py-1 rounded-md text-[10px] font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors shrink-0">{busyIp === ip.ip ? <Spinner /> : "Bloquear IP"}</button>}
          </div>
          {ip.active && ip.watching_title && (
            <p className="text-[11px] text-cyan-300 truncate" data-testid={`access-ip-watching-${acc.id}-${ip.ip}`}>▶ {ip.watching_title}{epTag(ip)}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default function Acessos() {
  const { user } = useAuth();
  const { t } = useLang();
  const [botId, setBotId] = useState("");
  const [botUser, setBotUser] = useState("");
  const [accs, setAccs] = useState(null);
  const [modal, setModal] = useState(false);
  const [del, setDel] = useState(null);
  const [editAcc, setEditAcc] = useState(null); // acesso em edição
  const [editForm, setEditForm] = useState({ name: "", username: "", password: "", max_concurrent: 1, allowed_ip: "", expires_at: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", password: "", max_concurrent: 1 });
  const [busy, setBusy] = useState(false);
  const [ipEdit, setIpEdit] = useState({});
  const [histOpen, setHistOpen] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [accountOwner, setAccountOwner] = useState(null);
  const [serverIp, setServerIp] = useState("");
  const [savingServerIp, setSavingServerIp] = useState(false);

  const [ipsOpen, setIpsOpen] = useState({});
  const [ipBusy, setIpBusy] = useState("");

  const toggleIpsPanel = async (a) => {
    const opening = !ipsOpen[a.id];
    setIpsOpen({ ...ipsOpen, [a.id]: opening });
    if (opening && a.ips === undefined) {
      try {
        const { data } = await api.get(`/instances/accesses/${a.id}/ips`);
        setAccs((prev) => (prev || []).map((x) => (x.id === a.id ? { ...x, ips: data.ips } : x)));
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const blockIp = async (a, ip) => {
    setIpBusy(ip);
    try {
      await api.post("/instances/ip-blocks", { ip });
      toast.success(`IP ${ip} bloqueado — deixa de assistir nos teus acessos`);
      setAccs((prev) => (prev || []).map((x) => (x.id === a.id ? { ...x, ips: (x.ips || []).map((r) => (r.ip === ip ? { ...r, blocked: true } : r)) } : x)));
    } catch (e) { toast.error(formatApiError(e)); } finally { setIpBusy(""); }
  };

  const unblockIp = async (a, ip) => {
    setIpBusy(ip);
    try {
      const { data } = await api.get("/instances/ip-blocks");
      const rows = (data || []).filter((b) => b.value === ip && b.active);
      await Promise.all(rows.map((b) => api.post(`/instances/ip-blocks/${b.id}/toggle`)));
      toast.success(`IP ${ip} desbloqueado`);
      setAccs((prev) => (prev || []).map((x) => (x.id === a.id ? { ...x, ips: (x.ips || []).map((r) => (r.ip === ip ? { ...r, blocked: false } : r)) } : x)));
    } catch (e) { toast.error(formatApiError(e)); } finally { setIpBusy(""); }
  };

  const saveIp = async (accId, ip) => {
    try {
      const { data } = await api.post(`/instances/accesses/${accId}/ip`, { ip: ip || null });
      toast.success(data.allowed_ip ? `Acesso travado no IP ${data.allowed_ip}` : "Acesso liberado para qualquer IP");
      setIpEdit({ ...ipEdit, [accId]: undefined });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const base = user.custom_domain ? `https://${user.custom_domain}` : window.location.origin;

  const load = useCallback(() => {
    if (accountId) {
      api.get(`/instances/account-accesses-detailed?user_id=${accountId}`)
        .then((r) => {
          setAccs(r.data.accesses);
          setAccountOwner(r.data.owner);
          setServerIp(r.data.owner?.allowed_ip || "");
        })
        .catch((e) => toast.error(formatApiError(e)));
      return;
    }
    if (!botId) return;
    api.get(`/instances/${botId}/accesses-detailed`)
      .then((r) => setAccs((r.data || []).map((a) => ({ ...a, ips: undefined }))))
      .catch((e) => toast.error(formatApiError(e)));
  }, [botId, accountId]);

  const saveServerIp = async (ip) => {
    setSavingServerIp(true);
    try {
      const { data } = await api.put(`/admin/users/${accountId}/server-ip`, { ip: ip || null });
      toast.success(data.allowed_ip ? `Conta travada no IP ${data.allowed_ip} — só esse servidor transmite` : "Conta liberada — qualquer IP pode assistir");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingServerIp(false);
    }
  };

  useEffect(() => {
    api.get("/bots").then((r) => {
      const list = r.data || [];
      const own = list.find((b) => b.owner_id === user.id) || list[0];
      if (own) {
        setBotId(own.id);
        setBotUser(own.username || "");
      }
    }).catch(() => {});
    if (user.role === "super_admin") {
      api.get("/saas/users").then((r) => setAccounts(r.data || [])).catch(() => {});
    }
  }, [user.id]);

  useEffect(() => {
    api.get(`/folders/tree${accountId ? `?user_id=${accountId}` : ""}`)
      .then((r) => setCategories((r.data.folders || []).map((f) => f.name).filter((n) => n !== "(Sem pasta)")))
      .catch(() => setCategories([]));
  }, [accountId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // "a assistir agora" ao vivo
    return () => clearInterval(t);
  }, [load]);

  const playlistUrl = (a) => `${base}/api/xc/get.php?username=${a.username}&password=${a.password}&type=m3u_plus`;
  const apiPhpUrl = (a) => `${base}/api/xc/player_api.php?username=${a.username}&password=${a.password}`;
  const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/instances/${botId}/accesses`, {
        name: form.name.trim(),
        username: form.username.trim() || null,
        password: form.password.trim() || null,
        max_concurrent: Math.max(1, Number(form.max_concurrent) || 1),
      });
      toast.success("Acesso criado! Envie a playlist ou os dados Xtream para o cliente.");
      setModal(false);
      setForm({ name: "", username: "", password: "", max_concurrent: 1 });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (a) => {
    try {
      await api.post(`/instances/accesses/${a.id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEditAcc = (a) => {
    setEditAcc(a);
    setEditForm({
      name: a.name || "",
      username: a.username || "",
      password: "",
      max_concurrent: a.max_concurrent ?? 1,
      allowed_ip: a.allowed_ip || "",
      expires_at: (a.expires_at || "").slice(0, 10),
    });
  };

  const saveEditAcc = async () => {
    if (!editAcc) return;
    setEditBusy(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        username: editForm.username.trim(),
        max_concurrent: Math.max(0, Number(editForm.max_concurrent) || 0),
        allowed_ip: editForm.allowed_ip.trim(),
        expires_at: editForm.expires_at || "",
      };
      if (editForm.password.trim()) payload.password = editForm.password.trim();
      await api.put(`/instances/accesses/${editAcc.id}`, payload);
      toast.success("Acesso atualizado");
      setEditAcc(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/instances/accesses/${del.id}`);
      toast.success("Acesso excluído");
      setDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="acessos-page">
      <PageHeader eyebrow={t("a_eyebrow")} title={t("acessos")} subtitle={t("a_sub")}>
        {!accountId && (
          <button data-testid="access-create-btn" onClick={() => setModal(true)} className="btn-neon px-5 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
            <Plus size={14} /> {t("a_new")}
          </button>
        )}
      </PageHeader>
      <DomainGate />

      <TutorialHint
        id="acessos"
        title={t("a_tutorial")}
        steps={[
          "Clique em Novo acesso: indique o nome do cliente, utilizador e senha (ou deixe o sistema gerar), e quantas conexões simultâneas ele pode usar.",
          "Envie para o cliente o link da Playlist (.m3u) ou os dados Xtream (servidor, utilizador e senha) — ambos ficam em cada card, prontos para copiar ou descarregar.",
          "O indicador verde pisca quando o cliente está a assistir agora; o contador mostra as conexões em uso.",
          "Desative um acesso com o botão de energia (o streaming dele para na hora) e reative quando quiser.",
          `Estes acessos são os mesmos da aba Acessos do seu bot (@${botUser || "..."}) — painel e bot sempre sincronizados.`,
        ]}
      />

      {user.role === "super_admin" && accounts.length > 0 && (
        <GlassCard className="p-4 space-y-3" data-testid="acessos-admin-block">
          <div className="flex flex-wrap items-center gap-3">
            <Users size={15} className="text-amber-400" />
            <span className="text-xs text-slate-400 font-mono2 uppercase tracking-widest">Admin — ver acessos da conta:</span>
            <select data-testid="acessos-account-select" className="input-dark rounded-lg px-3 py-2 text-sm" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Minha conta (bot atual)</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name || a.email}</option>
              ))}
            </select>
            {accountId && accountOwner && (
              <span className="text-[11px] text-slate-500" data-testid="acessos-account-owner">visualizando: <b className="text-slate-300">{accountOwner.name || accountOwner.email}</b></span>
            )}
          </div>
          {accountId && (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-800/60 pt-3" data-testid="acessos-server-ip">
              <Server size={14} className="text-cyan-400 shrink-0" />
              <span className="text-xs text-slate-400">IP fixo da conta:</span>
              <input
                data-testid="server-ip-input"
                className="input-dark rounded-lg px-3 py-2 text-xs font-mono2 w-44"
                placeholder="vazio = qualquer IP"
                value={serverIp}
                onChange={(e) => setServerIp(e.target.value)}
              />
              <button data-testid="server-ip-save" onClick={() => saveServerIp(serverIp.trim())} disabled={savingServerIp} className="btn-neon px-4 py-2 rounded-lg text-[11px]">
                {savingServerIp ? <Spinner /> : "Salvar"}
              </button>
              {accountOwner?.allowed_ip && (
                <button data-testid="server-ip-clear" onClick={() => { setServerIp(""); saveServerIp(null); }} disabled={savingServerIp} className="px-4 py-2 rounded-lg text-[11px] border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors">
                  Liberar qualquer IP
                </button>
              )}
              <p className="w-full text-[10px] font-mono2 text-slate-600">
                Com IP definido, os acessos dessa conta só tocam a partir desse IP (ex: o IP do servidor dela). Vazio = qualquer IP.
                {accountOwner?.allowed_ip ? <span className="text-amber-300"> Atual: {accountOwner.allowed_ip}</span> : " Atual: sem restrição."}
              </p>
            </div>
          )}
        </GlassCard>
      )}

      {!accs ? (
        <div className="flex justify-center py-14"><Spinner /></div>
      ) : !accs.length ? (
        <EmptyState icon={KeyRound} title={t("a_empty")} subtitle={t("a_empty_sub")} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4 [&>*]:min-w-0" data-testid="acessos-list">
          {accs.map((a) => (
            <GlassCard key={a.id} className="p-5 space-y-3" data-testid={`access-card-${a.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
                  <KeyRound size={15} className="text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold truncate flex items-center gap-2">
                    {a.name || a.username}
                    {a.watching && <span className="inline-flex items-center gap-1 text-[9px] font-mono2 uppercase tracking-widest text-emerald-300" data-testid={`access-watching-${a.id}`}><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> a assistir</span>}
                    {a.share_alert && <span title={`Esta credencial foi usada por ${a.share_alert.ip_count} IPs distintos nas últimas 24h — possível partilha`} className="inline-flex items-center gap-1 text-[9px] font-mono2 uppercase tracking-widest text-amber-300" data-testid={`access-share-alert-${a.id}`}><ShieldBan size={10} /> partilha? · {a.share_alert.ip_count} IPs</span>}
                  </p>
                  <p className="text-[11px] font-mono2 text-slate-500 truncate">{a.username} · {a.active_streams || 0}/{a.max_concurrent || 1} conexões</p>
                </div>
                <button data-testid={`access-edit-${a.id}`} onClick={() => openEditAcc(a)} title="Editar acesso (nome, credenciais, conexões, IP, validade)" className="p-2 rounded-lg text-slate-500 hover:text-amber-300 hover:bg-amber-400/10 transition-colors">
                  <Pencil size={15} />
                </button>
                <button data-testid={`access-toggle-${a.id}`} onClick={() => toggle(a)} title={a.status === "active" ? "Desativar" : "Ativar"} className={`p-2 rounded-lg transition-colors ${a.status === "active" ? "text-emerald-300 hover:bg-emerald-400/10" : "text-slate-600 hover:bg-slate-800"}`}>
                  <Power size={15} />
                </button>
                <button data-testid={`access-delete-${a.id}`} onClick={() => setDel(a)} title="Excluir" className="p-2 rounded-lg text-slate-500 hover:text-rose-300 hover:bg-rose-400/10 transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>

              <div className="rounded-xl bg-slate-950/50 border border-slate-800/60 px-3.5 py-2.5 space-y-1.5 text-[11px] font-mono2 overflow-hidden">
                <p className="text-slate-400 flex items-center justify-between gap-2">usuário: <span className="text-slate-200 min-w-0 truncate">{a.username}</span></p>
                <p className="text-slate-400 flex items-center justify-between gap-2">senha: <span className="text-slate-200 min-w-0 truncate">{a.password}</span></p>
                <p className="text-slate-400 flex items-center justify-between gap-2">servidor: <span className="text-slate-200 min-w-0 truncate">{base}/api/xc</span></p>
              </div>

              <div className="rounded-xl bg-slate-950/50 border border-slate-800/60 px-3.5 py-2.5 space-y-1.5 text-[11px] font-mono2 overflow-hidden" data-testid={`access-security-${a.id}`}>
                <p className="text-slate-400 flex items-center justify-between gap-2">
                  trava de IP:
                  {a.allowed_ip
                    ? <span className="text-amber-300 min-w-0 truncate" data-testid={`access-ip-lock-${a.id}`}>🔒 {a.allowed_ip}</span>
                    : <span className="text-emerald-300" data-testid={`access-ip-lock-${a.id}`}>livre (qualquer IP)</span>}
                </p>
                <p className="text-slate-400 flex items-center justify-between gap-2">último acesso: <span className="text-slate-200 min-w-0 truncate">{fmtDT(a.last_access_at)}</span></p>
                <p className="text-slate-400 flex items-center justify-between gap-2">último IP: <span className="text-slate-200 min-w-0 truncate">{a.last_ip || "—"}</span></p>
                <p className="text-slate-400 flex items-center justify-between gap-2">dispositivo: <span className="text-slate-200 min-w-0 truncate">{a.last_device || "—"}</span></p>
                {ipEdit[a.id] !== undefined ? (
                  <div className="flex flex-wrap items-center gap-2 pt-1" data-testid={`access-ip-editor-${a.id}`}>
                    <input className="input-dark rounded-lg px-2.5 py-1.5 text-[11px] font-mono2 flex-1 min-w-32" placeholder="200.100.50.25 (vazio = livre)" value={ipEdit[a.id]} onChange={(e) => setIpEdit({ ...ipEdit, [a.id]: e.target.value })} data-testid={`access-ip-input-${a.id}`} />
                    <button data-testid={`access-ip-save-${a.id}`} onClick={() => saveIp(a.id, ipEdit[a.id].trim())} className="btn-neon px-3 py-1.5 rounded-lg text-[10px]">Salvar</button>
                    {a.last_ip && ipEdit[a.id].trim() !== a.last_ip && (
                      <button data-testid={`access-ip-use-last-${a.id}`} onClick={() => saveIp(a.id, a.last_ip)} className="px-3 py-1.5 rounded-lg text-[10px] border border-cyan-400/30 text-cyan-300">Usar último IP</button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button data-testid={`access-ip-edit-${a.id}`} onClick={() => setIpEdit({ ...ipEdit, [a.id]: a.allowed_ip || "" })} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-slate-700 text-slate-300 hover:border-amber-400/40 hover:text-amber-300 transition-colors flex items-center gap-1.5">
                      <Shield size={11} /> {a.allowed_ip ? "Trocar IP da trava" : "Travar num IP"}
                    </button>
                    {a.allowed_ip && (
                      <button data-testid={`access-ip-clear-${a.id}`} onClick={() => saveIp(a.id, null)} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-slate-700 text-slate-300 hover:border-emerald-400/40 hover:text-emerald-300 transition-colors">Liberar qualquer IP</button>
                    )}
                    {(a.history || []).length > 0 && (
                      <button data-testid={`access-history-${a.id}`} onClick={() => setHistOpen({ ...histOpen, [a.id]: !histOpen[a.id] })} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-slate-700 text-slate-300 hover:text-white transition-colors flex items-center gap-1.5">
                        <History size={11} /> Histórico ({a.history.length})
                      </button>
                    )}
                    <button data-testid={`access-ips-${a.id}`} onClick={() => toggleIpsPanel(a)} title="Ver os IPs que usam esta credencial — quem está a ver agora, ISP, e bloquear" className="px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-slate-700 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors flex items-center gap-1.5">
                      <Users size={11} /> IPs
                    </button>
                  </div>
                )}
                {ipsOpen[a.id] && <AccessIps acc={a} onBlock={(ip) => blockIp(a, ip)} onUnblock={(ip) => unblockIp(a, ip)} busyIp={ipBusy} />}
                {histOpen[a.id] && (
                  <div className="pt-1 space-y-1 border-t border-slate-800/60" data-testid={`access-history-list-${a.id}`}>
                    {[...(a.history || [])].reverse().slice(0, 8).map((h, i) => (
                      <p key={i} className={`flex items-center justify-between gap-2 ${h.action === "blocked" ? "text-rose-300" : "text-slate-500"}`}>
                        <span className="min-w-0 truncate">{h.action === "blocked" ? "⛔ bloqueado" : h.action} · {h.ip || "?"} · {h.device || "?"}</span>
                        <span className="shrink-0">{fmtDT(h.at)}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button data-testid={`access-copy-playlist-${a.id}`} onClick={() => copy(playlistUrl(a), "Link da playlist copiado!")} className="px-3 py-2 rounded-lg text-[11px] font-semibold border border-slate-700 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors flex items-center gap-1.5">
                  <Copy size={12} /> Playlist M3U
                </button>
                <a data-testid={`access-download-playlist-${a.id}`} href={playlistUrl(a)} download className="px-3 py-2 rounded-lg text-[11px] font-semibold border border-slate-700 text-slate-300 hover:border-sky-400/40 hover:text-sky-300 transition-colors flex items-center gap-1.5">
                  <Download size={12} /> Baixar .m3u
                </a>
                <button data-testid={`access-copy-xtream-${a.id}`} onClick={() => copy(`Servidor: ${base}/api/xc\nUsuário: ${a.username}\nSenha: ${a.password}\nAPI: ${apiPhpUrl(a)}`, "Dados Xtream copiados!")} className="px-3 py-2 rounded-lg text-[11px] font-semibold border border-slate-700 text-slate-300 hover:border-violet-400/40 hover:text-violet-300 transition-colors flex items-center gap-1.5">
                  <Wifi size={12} /> Dados Xtream
                </button>
                {categories.length > 0 && (
                  <select
                    data-testid={`access-download-cat-${a.id}`}
                    value=""
                    onChange={(e) => {
                      const cat = e.target.value;
                      if (!cat) return;
                      window.open(`${playlistUrl(a)}&category=${encodeURIComponent(cat)}`, "_blank", "noopener");
                    }}
                    title="Baixar playlist apenas de uma categoria"
                    className="px-3 py-2 rounded-lg text-[11px] font-semibold border border-slate-700 bg-transparent text-slate-300 hover:border-emerald-400/40 hover:text-emerald-300 transition-colors cursor-pointer"
                  >
                    <option value="" disabled className="bg-slate-900">Por categoria…</option>
                    {categories.map((c) => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
                  </select>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Novo acesso de cliente">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nome do cliente</label>
            <input data-testid="access-name-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Ex: João da Silva" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Usuário (opcional)</label>
              <input data-testid="access-username-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="automático" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Senha (opcional)</label>
              <input data-testid="access-password-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="automática" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">{t("a_connections")}</label>
            <input data-testid="access-maxconn-input" type="number" min="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.max_concurrent} onChange={(e) => setForm({ ...form, max_concurrent: e.target.value })} />
          </div>
          <button data-testid="access-save-btn" onClick={create} disabled={busy || !form.name.trim()} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
            {busy ? <Spinner /> : <><Plus size={15} /> Criar acesso</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!del} onClose={() => setDel(null)} title={`Excluir acesso de "${del?.name || del?.username}"?`}>
        <div className="space-y-4">
          <p className="text-sm text-slate-300">O streaming desse cliente para na hora e a playlist dele deixa de funcionar. Não tem volta.</p>
          <button data-testid="access-delete-confirm" onClick={remove} disabled={busy} className="w-full px-4 py-3 rounded-xl text-sm font-semibold border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors flex items-center justify-center gap-2">
            {busy ? <Spinner /> : <><Trash2 size={14} /> Excluir acesso</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!editAcc} onClose={() => setEditAcc(null)} title={`Editar acesso — ${editAcc?.name || editAcc?.username || ""}`} testid="access-edit-modal">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nome do cliente</label>
            <input data-testid="access-edit-name" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Usuário</label>
              <input data-testid="access-edit-username" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Conexões simultâneas</label>
              <input data-testid="access-edit-maxconn" type="number" min="0" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={editForm.max_concurrent} onChange={(e) => setEditForm({ ...editForm, max_concurrent: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nova senha (deixe em branco para manter)</label>
            <input data-testid="access-edit-password" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="mínimo 8 caracteres" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} autoComplete="new-password" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Travar num IP</label>
              <input data-testid="access-edit-ip" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="vazio = qualquer IP" value={editForm.allowed_ip} onChange={(e) => setEditForm({ ...editForm, allowed_ip: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Validade</label>
              <input data-testid="access-edit-expiry" type="date" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={editForm.expires_at} onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })} />
            </div>
          </div>
          <p className="text-[10px] font-mono2 text-slate-600">Ao mudar usuário/senha/IP, o streaming ativo do cliente é cortado e ele volta a ligar com os dados novos.</p>
          <button data-testid="access-edit-save" onClick={saveEditAcc} disabled={editBusy || !editForm.name.trim() || !editForm.username.trim()} className="btn-neon w-full px-4 py-3 rounded-xl text-sm flex items-center justify-center gap-2">
            {editBusy ? <Spinner /> : <><Pencil size={14} /> Guardar alterações</>}
          </button>
        </div>
      </Modal>
    </div>
  );
}
